/**
 * Account settings, access control, the backend management landing page and its
 * homepage editor, the agent sign-in page and the "AI can make mistakes" help page.
 * Keys must match ../en/settings.ts.
 * Agent names come from AgentContext and are not here.
 */
const settings = {
  account: {
    title: '账户信息与设置',
    infoHeading: '账户信息',
    name: '账户名称',
    login: '登录名',
    activeAgent: '当前 Agent',
    role: '角色',
    admin: '管理员',
    editInBackend: '在后台编辑',
    tabsLabel: '账户设置分区',
    tabCapabilities: '能力',
    tabMemory: '记忆',
    tabKnowledge: '知识库',
  },
  capabilities: {
    heading: 'MCP 服务',
    tools: '{{n}} 个工具',
    write: '{{n}} 个写操作',
    read: '{{n}} 个读操作',
    connected: '已连接',
  },
  knowledge: {
    intro: '这些是 Agent 回答问题时用到的知识，由平台维护。',
    agent: 'Agent',
    loading: '加载中…',
    loadFailed: '知识库加载失败。',
    retry: '重试',
    forbidden: '你没有这个 Agent 的访问权限。',
    empty: '这个 Agent 还没有接入知识库。',
    documents: '{{n}} 篇文档',
    lastSynced: '最近同步：{{time}}',
    status: { succeeded: '已同步', syncing: '同步中', failed: '同步失败' },
    failedHint: '最近一次同步失败，Agent 仍会使用上一次同步成功的内容。',
    domainsTitle: '领域知识',
    domainsIntro: '经过整理的结构化知识（数据口径、查询方法、业务规则），Agent 按需查阅。',
    kbTitle: '资料库',
    kbIntro: '文档资料，Agent 回答时检索相关内容，定期同步。',
    domainsEmpty: '这个 Agent 还没有领域知识。',
    domainsLoadFailed: '领域知识加载失败。',
    lastPublished: '最近发布：{{time}}',
    scope: { agent: '专属', shared: '共享' },
  },
  memory: {
    agent: 'Agent',
    type: '类型',
    category: '分类',
    typeAll: '全部',
    typeLtm: '长期记忆',
    typeEpisodic: '情景记忆',
    typeReflection: '反思',
    categoryAll: '全部',
    refresh: '刷新',
    loadingShort: '加载中…',
    loading: '正在加载记忆…',
    loadFailed: '加载记忆失败',
    empty: '这个 Agent 还没有记忆记录。',
    noContent: '（无内容）',
    loadMore: '加载更多',
  },
  access: {
    breadcrumbHome: 'Rocky',
    breadcrumbRbms: 'Rocky 后台管理系统',
    breadcrumbAccess: '权限管理',
    title: '{{agent}} 权限管理',
    agentLabel: 'Agent',
    kinds: {
      user: { label: '个人', idLabel: 'Alias', idHint: '要授权的 Amazon alias（例如 jiananj），立即生效。' },
      team: {
        label: '团队',
        idLabel: 'Abacus 团队 ID',
        idHint: 'amzn1.abacus.team.…，团队所有成员都会获得权限，成员变更 24 小时内同步。',
      },
      ldap: { label: 'LDAP 组', idLabel: 'LDAP 组', idHint: '成员通过登录 token 立即获得权限。' },
      posix: { label: 'POSIX 组', idLabel: 'POSIX 组', idHint: '成员变更 24 小时内同步。' },
    },
    anyType: '全部类型',
    anyAccess: '全部权限',
    canManage: '可管理',
    useOnly: '仅使用',
    forbidden: '你没有这个 Agent 的管理权限。',
    unreachable: '无法连接服务器，请检查网络。',
    saveFailed: '保存授权失败。',
    revokeFailed: '撤销权限失败。',
    updatedHeader: '权限已更新',
    grantedHeader: '已授权',
    updatedBody: '授权已更新。',
    savedBody: '授权已保存。',
    errorHeader: '出错了',
    columns: {
      name: '名称',
      type: '类型',
      access: '权限',
      grantedBy: '授权人',
      grantedAt: '授权时间',
      expiresAt: '过期时间',
      action: '操作',
    },
    manage: '管理',
    use: '使用',
    edit: '编辑',
    remove: '移除',
    removeSelected: '移除（{{n}}）',
    emptyTitle: '暂无授权',
    emptyBody: '还没有团队、组或个人获得这个 Agent 的权限。',
    noMatchTitle: '没有匹配项',
    noMatchBody: '没有符合搜索条件的授权。',
    loading: '正在加载权限列表…',
    tableTitle: '授权列表',
    tableDescription: '谁可以使用这个 Agent、谁可以管理它的权限。移除一行即撤销授权。',
    infoHeader: '权限管理',
    infoBody:
      '每一行是一条授权。“使用”可以选择这个 Agent 并与它对话；“管理”还可以编辑这份列表。个人和 LDAP 组的授权立即生效；团队和 POSIX 组的成员变更 24 小时内同步。移除授权立即生效。',
    info: '说明',
    addGrant: '添加授权',
    search: '按名称或 ID 搜索',
    matches: '{{n}} 个匹配',
    filterType: '按类型筛选',
    filterAccess: '按权限筛选',
    preferences: '偏好设置',
    confirm: '确定',
    cancel: '取消',
    pageSize: '每页条数',
    editTitle: '编辑权限：{{name}}',
    save: '保存',
    grantAccess: '授权',
    formType: '类型',
    displayName: '显示名称',
    displayNameHint: '可选，在列表中代替 ID 显示。',
    formAccess: '权限',
    useDescription: '可以选择这个 Agent 并与它对话。',
    manageDescription: '可以使用这个 Agent，并编辑谁有权限。',
  },
  rbms: {
    title: 'Rocky 后台管理系统',
    subtitle: '统一管理 Rocky 平台上各个 Agent 的权限和工作区首页。',
    deniedTitle: '无权访问',
    deniedBody: '需要是 <code>{{group}}</code> 的成员才能访问此页面。',
    accessTitle: '权限管理',
    accessDescription: '管理谁可以查看和编辑每个 Agent：添加组或个人、分配角色、查看成员状态。',
    homepageTitle: '编辑工作区首页',
    homepageDescription:
      '配置 Agent 工作区首页：页面标题、热门问题和热门话题卡片。',
    cpaTitle: 'CPA',
    cpaDescription:
      '配置 Credit Program Advisor 的决策树、项目参数、资格规则与叠加关系，并管理发布版本。',
  },
  cpa: {
    tabTree: '决策树',
    tabPrograms: '项目参数',
    tabRules: '规则',
    tabStacking: '叠加关系',
    tabVersions: '版本',
  },
  login: {
    brand: 'Rocky Agent',
    authenticating: '正在验证',
    resuming: '正在恢复',
    welcome: '欢迎',
    failed: '验证失败',
    signingIn: '正在登录…',
    signedIn: '登录成功',
    resumingSession: '正在恢复你的 Agent 会话…',
    signedInAs: '已登录为 <strong>{{user}}</strong>',
    success: '验证成功',
    canClose: '现在可以关闭此窗口。',
    tryAgain: '重试',
    footer: '通过 Federate 安全认证',
    errors: {
      noToken: '没有可用的身份令牌，请重新登录。',
      noState: '没有找到认证状态，请回到 Slack 重新登录。',
      missing_state: '认证链接不完整，请回到 Slack 重新登录。',
      session_expired: '认证链接已过期或已被使用，请回到 Slack 重试。',
      already_consumed: '认证链接已被使用，请回到 Slack 重试。',
      unknown_channel: '找不到对应的 Agent，请联系管理员。',
      store_unavailable: '服务暂时不可用，请稍后重试。',
      auth_failed: '认证失败，请重试。',
      resumeFailed: '恢复会话失败',
      unexpected: '恢复 Agent 会话失败，请重试。',
    },
  },
  about: {
    title: 'Rocky 的回答不准确或有误导性，这是怎么回事？',
    lede: 'Rocky 是一个 AI Agent。和所有生成式 AI 助手一样，它会尽力提供帮助，但有时给出的信息可能不完整、过时，甚至是错误的。本页说明出现这种情况的原因，以及如何获得最可靠的结果。',
    whyHeading: 'Rocky 为什么会给出错误的回答',
    why1: '<strong>幻觉。</strong>Rocky 可能生成看起来自信、权威，但没有事实依据的内容。这是当今前沿生成式 AI 模型的已知局限：输出“看似正确，实则有误”。',
    why2: '<strong>知识与数据的局限。</strong>Rocky 底层模型有训练截止时间，可能不了解最近的变化。当它使用已连接的工具、技能或内部数据源时，回答的时效性和完整性取决于这些来源。',
    why3: '<strong>工具与检索的缺口。</strong>Rocky 可能会调用工具或检索内部系统来回答你。如果工具只返回了部分结果、超时，或者没有访问所需数据的权限，回答就可能不完整，而 Rocky 不一定会提示。',
    why4: '<strong>表述有歧义。</strong>如果请求可以有多种理解，Rocky 可能会猜测你的意图，并朝着你并不想要的方向自信地作答。',
    doHeading: '你应该怎么做',
    do1: '<strong>不要把 Rocky 当作唯一的信息来源。</strong>重要的内容务必核实，尤其是在据此行动或分享给他人之前。',
    do2: '<strong>查看来源和工具输出。</strong>当 Rocky 引用文档、工单或系统时，直接打开原文。原文可能包含摘要中省略的上下文。',
    do3: '<strong>让 Rocky 展示过程。</strong>要求它列出步骤、使用的工具或依据的具体记录，方便你核对。',
    do4: '<strong>提交反馈。</strong>使用回答下方的反馈按钮，标记没有帮助或不正确的内容。你的反馈会帮助 Rocky 变得更好。',
    footnote: 'Rocky 是为了帮你更快地工作，而不是取代你自己的判断。当准确性很重要时，请先确认再使用。',
  },
  homepageEditor: {
    englishHint: '英文可以不填，留空的部分会显示中文。',
    editingFor: '正在编辑首页：<strong>{{agent}}</strong>',
    loading: '正在加载配置…',
    part: '第 {{n}} 部分',
    edit: '编辑',
    badgeNew: '新',
    badgeHot: '热门',
    conflict: '该首页刚被其他人更新。已重新加载最新版本，请重新修改后再发布。',
    reloadFailed: '重新加载最新配置失败，请刷新页面后重试。',
    saveFailed: '保存失败，请重试。',
    cancel: '取消',
    publish: '发布',
    optional: '（选填）',
    maxWords: '最多 {{n}} 字',
    maxWordsLines: '最多 {{n}} 字，最多 {{lines}} 行',
    tooManyLines: '最多 {{n}} 行，请删除多余的换行。',
    fontWeight: '字重',
    weightBold: '粗体',
    weightRegular: '常规',
    linkDisabled: '链接（此处不可配置）',
    textColor: '文字颜色',
    color: '颜色',
    setColor: '设置颜色',
    colorNone: '无',
    hex: 'Hex',
    hexValue: 'Hex 颜色值',
    pageHeader: '页面标题',
    subheading: '副标题',
    badge: '标签：',
    addQuestion: '新增问题',
    question: '热门问题 {{n}}',
    removeQuestion: '删除问题 {{n}}',
    moveQuestionUp: '上移问题 {{n}}',
    moveQuestionDown: '下移问题 {{n}}',
    dragQuestion: '拖动以调整问题 {{n}} 的顺序',
    uiCopy: '展示文案',
    uiCopyPlaceholder: '例如：我这个季度的 pipeline 怎么样？',
    prompt: '提示词',
    addTopic: '新增话题',
    topic: '热门话题 {{n}}',
    removeTopic: '删除话题 {{n}}',
    moveTopicUp: '上移话题 {{n}}',
    moveTopicDown: '下移话题 {{n}}',
    dragTopic: '拖动以调整话题 {{n}} 的顺序',
    iconImage: '图标',
    topicTitle: '话题标题',
    topicTitlePlaceholder: '我的 quota 能完成吗？',
    description: '描述文字',
    link: '链接',
    linkHint: '填写后，点击卡片会打开此链接，而不是发送提示词。支持站内路径（如 /cheetah）或 http(s):// 地址。',
    linkLabel: '话题 {{n}} 的链接',
    linkError: '请输入以 / 开头的站内路径（如 /cheetah）或完整的 http(s):// 地址。',
    chooseFiles: '选择文件',
    uploading: '上传中…',
    removeFile: '移除文件',
    icon: {
      type: '仅支持 PNG / JPEG / WebP 图片',
      tooLarge: '文件过大（{{kb}} KB），需 ≤ 512 KB',
      unreadable: '无法读取图片，文件可能已损坏',
      tooSmall: '尺寸过小（{{w}}×{{h}}），至少 {{min}}×{{min}}',
      notSquare: '图片非正方形（{{w}}×{{h}}），显示时可能被裁切',
      uploadFailed: '上传失败，请重试',
    },
  },
};

export default settings;
